import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IdentityService } from 'src/app/core/identity.service';
import { GridComponent } from 'src/app/shared/grid/grid.component';
import { FetchArgs } from 'src/app/shared/models/core';
import { GridComponentService } from 'src/app/shared/grid/grid-component.service';
import { WINDOW } from 'src/app/core/core.module';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { RecruitingActions } from 'src/app/root-store/recruiting-store';
import * as recruitingSelectors from 'src/app/root-store/recruiting-store/selectors';
import { ExpertiseModel } from 'src/app/shared/models/expertisemodel.model';
import { OptionModel } from 'src/app/shared/grid/filters/option.model';
import { ImportMarginGridItemModel } from './direct-offer-Import-margin-gridItem.model';
import { ImportMarginGridData } from './direct-offer-Import-margin-grid-data.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'nova-direct-offer-import-margin-dialog',
    templateUrl: './direct-offer-import-margin-dialog.component.html',
    styleUrls: ['./direct-offer-import-margin-dialog.component.scss']
})
export class DirectOfferImportMarginDialogComponent implements OnInit {
    gridName = 'nova-recruiting-margins-grid';
    isImportMarginButtonDisabled = true;
    isImporting = false;

    get defaultFetchArgs(): FetchArgs {
        return {
            filter: { facilityId: this.facilityId },
            sort: {
                field: 'createdDate',
                dir: 'desc'
            },
            pageSize: 25,
            skip: 0
        } as FetchArgs;
    }
    statusOptions: OptionModel[] = DirectOfferImportMarginDialogComponent.getStatusOptions();

    @ViewChild(GridComponent, { static: true }) novaGrid: GridComponent;

    facilityId: number = null;
    title: string = '';
    importFromMarginId: number;

    fetchArgs$: BehaviorSubject<FetchArgs>;
    data$: Observable<ImportMarginGridData[]>;
    lookupExpertises$: Observable<ExpertiseModel[]>;
    isLoading$: Observable<boolean>;

    constructor(
        private readonly identityService: IdentityService,
        private readonly gridService: GridComponentService,
        private readonly _store: Store,
        @Inject(WINDOW) private readonly _window: Window,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly _dialogRef: MatDialogRef<DirectOfferImportMarginDialogComponent>
    ) {
        this.facilityId = data?.facilityId;
        this.title = `Import Margin - ${data?.facilityName}`;
    }

    ngOnInit(): void {
        this.loadState();
        this.isLoading$ = this._store.select(recruitingSelectors.selectMarginsIsLoading);
        this.data$ = this._store.select(recruitingSelectors.selectMargins);
        this.lookupExpertises$ = this._store.select(recruitingSelectors.selectMarginsLookups);
        this._store
            .select(recruitingSelectors.selectMarginsGridSearchQuery)
            .pipe(untilDestroyed(this))
            .subscribe((args) => (this.fetchArgs$ = new BehaviorSubject<FetchArgs>(args)));
    }

    load($event) {
        this.isImportMarginButtonDisabled = true;
        this._store.dispatch(
            RecruitingActions.DirectOfferImportMarginActions.setMarginsGridSearchQuery({ searchQuery: $event })
        );
    }

    onClickClearFilter(): void {
        this.novaGrid.clearGridState();
    }

    onMarginIdClick(row: ImportMarginGridItemModel): void {
        this.openMarginCalculator(row.id);
    }

    private openMarginCalculator(marginId: number): void {
        this._window.open(
            `${this._window.location.origin}/#/margin-calculator/${this.identityService.current.contactId}/margin/${marginId}`,
            '_blank',
            'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
        );
    }

    closeDialog(): void {
        this._dialogRef.close();
    }

    importMargin(): void {
        if (!this.isImporting) {
            this.isImporting = true;
            this._store.dispatch(
                RecruitingActions.DirectOfferImportMarginActions.createDirectOfferMarginViaImport({
                    offerId: this.data?.offerId,
                    importFromMarginId: this.importFromMarginId
                })
            );

            this._store
                .select(recruitingSelectors.selectCreatedDirectOfferMarginViaImport)
                .pipe(untilDestroyed(this))
                .subscribe((marginId) => {
                    if (marginId) {
                        this._store.dispatch(
                            RecruitingActions.DirectOfferImportMarginActions.resetMarginId({ marginId: undefined })
                        );
                        this._dialogRef.close(marginId);
                    }
                });
        }
    }

    rowSelected(item: ImportMarginGridItemModel[]): void {
        this.importFromMarginId = item[0].id;
        this.isImportMarginButtonDisabled = false;
    }

    loadState() {
        const userFetchArgs = this.gridService.getFetchArgs(this.gridName);
        this.fetchArgs$ = new BehaviorSubject<FetchArgs>(userFetchArgs || this.defaultFetchArgs);
        this._store.dispatch(
            RecruitingActions.DirectOfferImportMarginActions.setMarginsGridSearchQuery({
                searchQuery: this.fetchArgs$.value
            })
        );
    }

    private static getStatusOptions(): OptionModel[] {
        return [
            { id: '', name: '(Blank)' },
            { id: 'CAN', name: 'CAN' },
            { id: 'COM', name: 'COM' },
            { id: 'PRE', name: 'PRE' },
            { id: 'REJ', name: 'REJ' },
            { id: 'WOR', name: 'WOR' }
        ];
    }
}
